// §2.12: typed API client. `fetch` with credentials:"include" (session cookies), JSON
// body, throws ApiFail parsed from the §2.3 error envelope, and on a 401 whose code is
// "unauthorized" (not e.g. "invalid_credentials", which is also a 401 — §2.3) calls
// POST /api/v1/auth/refresh once and retries the original request once.

import type { ApiErrorEnvelope } from "./types";

const API_BASE = "/api/v1";

export type Method = "GET" | "POST" | "PATCH" | "DELETE" | "PUT";

/**
 * Optional API origin / fetch override so the Chrome extension and the Tauri
 * desktop shell (SD7) can reuse this client and `UploadQueue` against the Grackle
 * host. The web app and `cargo tauri dev` (Vite proxy) leave both unset and
 * keep same-origin `/api/v1` behaviour. Packaged desktop builds call
 * `configureApi({ origin })` from `desktop.ts` after `desktop_info`.
 *
 * FR-X6: the extension still authenticates with the Grackle web session (cookies), never
 * a long-lived API key. FR-X5: host permission is only the Grackle origin, not `<all_urls>`.
 */
export interface ApiClientOptions {
  /** Absolute origin, no trailing slash. Empty means same-origin (the web app). */
  origin?: string;
  /** Injected fetch — the extension routes cookie-auth calls through a Grackle-tab bridge
   * when a privileged extension request does not carry SameSite=Lax cookies. */
  fetchImpl?: typeof fetch;
}

let apiOrigin = "";
let apiFetch: typeof fetch = fetch;

export function configureApi(options: ApiClientOptions): void {
  if (options.origin !== undefined) {
    apiOrigin = options.origin.replace(/\/$/, "");
  }
  if (options.fetchImpl) {
    apiFetch = options.fetchImpl;
  }
}

/** The configured absolute API origin, or `""` for same-origin (browser, or desktop `tauri
 * dev` via the Vite proxy). Exported so `ws.ts` can build an absolute `wss://` URL instead of
 * resolving against `window.location.href`, which in the packaged shell is `tauri://localhost`
 * — not the API host. */
export function getApiOrigin(): string {
  return apiOrigin;
}

/**
 * FR-A4: how the desktop shell's `Authorization: Bearer` access token (held in Rust memory
 * only — never this module) and its one-shot refresh are reached from here. Set by
 * `desktop.ts::loadDesktopInfo` once a packaged build resolves a non-empty API origin; `null`
 * in the browser and in `tauri dev` (empty origin, same-origin cookies as today).
 */
export interface DesktopAuthBridge {
  getAccessToken(): Promise<string | null>;
  /** Attempts one refresh via the keychain refresh token. Resolves `false` if there is no
   * stored refresh token or the server rejected it (revoked/expired session). */
  refresh(): Promise<boolean>;
}

let desktopAuth: DesktopAuthBridge | null = null;

export function configureDesktopAuth(bridge: DesktopAuthBridge | null): void {
  desktopAuth = bridge;
}

export function getDesktopAuthBridge(): DesktopAuthBridge | null {
  return desktopAuth;
}

export class ApiFail extends Error {
  readonly code: string;
  readonly status: number;
  /** e.g. `{org_slug: "acme"}` on a `sso_required` signin failure (§2.3's `error.data`). */
  readonly data: Record<string, unknown> | undefined;

  constructor(code: string, message: string, status: number, data?: Record<string, unknown>) {
    super(message);
    this.name = "ApiFail";
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

function resolveUrl(path: string): string {
  // Dev blob routes (§2.6) live at root, not under /api/v1; everything else does.
  if (path.startsWith("/dev/") || path.startsWith("http://") || path.startsWith("https://")) {
    return path.startsWith("/") && apiOrigin ? `${apiOrigin}${path}` : path;
  }
  return `${apiOrigin}${API_BASE}${path}`;
}

async function rawFetch(
  method: Method,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<Response> {
  const init: RequestInit = { method };
  const headers: Record<string, string> = { ...extraHeaders };

  if (desktopAuth && apiOrigin) {
    // FR-A4: the packaged webview's origin (tauri://localhost / tauri.localhost) is never
    // same-origin with the API, so there is no cookie jar worth sending — and sending one
    // across origins would need `credentials: "include"` plus a CORS allow-credentials the
    // server deliberately never grants (`lib.rs::cors_layer`). Bearer only.
    init.credentials = "omit";
    const token = await desktopAuth.getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  } else {
    init.credentials = "include";
  }

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  init.headers = headers;
  return apiFetch(resolveUrl(path), init);
}

/** A share-locked/link-unavailable style raw body: `{"error": "<code>", ...rest}` — a flat
 * string `error`, not the §2.3 `{"error":{"code","message"}}` envelope. FR-SH5a's five gate
 * codes (`link_unavailable`, `share_locked`, `email_not_allowed`, `invalid_password`,
 * `invalid_code`) all render this shape (error.rs's `IntoResponse` impl) so the gate UI can
 * branch on `ApiFail.code` without parsing a message string. */
interface FlatErrorBody {
  error: string;
  message?: string;
  [key: string]: unknown;
}

function isFlatErrorBody(parsed: unknown): parsed is FlatErrorBody {
  return !!parsed && typeof (parsed as { error?: unknown }).error === "string";
}

async function toApiFail(res: Response): Promise<ApiFail> {
  let code = "internal";
  let message = res.statusText || "request failed";
  let data: Record<string, unknown> | undefined;
  try {
    const parsed = (await res.json()) as unknown;
    if (isFlatErrorBody(parsed)) {
      code = parsed.error;
      if (typeof parsed.message === "string") message = parsed.message;
      const { error: _code, message: _message, ...rest } = parsed;
      if (Object.keys(rest).length > 0) data = rest;
    } else {
      const envelope = parsed as ApiErrorEnvelope;
      if (envelope?.error?.code) code = envelope.error.code;
      if (envelope?.error?.message) message = envelope.error.message;
      if (envelope?.error?.data) data = envelope.error.data;
    }
  } catch {
    // Body wasn't the JSON envelope (e.g. a proxy error page) — keep the defaults above.
  }
  return new ApiFail(code, message, res.status, data);
}

// Coalesce concurrent refresh attempts into a single in-flight request.
let refreshInFlight: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = rawFetch("POST", "/auth/refresh")
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

// FR-A4 (desktop/mobile shell): the same coalescing for the bridge's refresh. Every request
// that 401s at once (a cold start fires /auth/me, the tray list, notifications... together)
// must share ONE refresh: the server rotates the refresh token on use and treats a second use
// of the old one as theft, revoking the session (`rotate_refresh_token`'s reuse detection).
let desktopRefreshInFlight: Promise<boolean> | null = null;

function desktopRefreshOnce(bridge: DesktopAuthBridge): Promise<boolean> {
  if (!desktopRefreshInFlight) {
    desktopRefreshInFlight = bridge.refresh().finally(() => {
      desktopRefreshInFlight = null;
    });
  }
  return desktopRefreshInFlight;
}

/**
 * Renew the session once (coalesced with any refresh already in flight) and report whether a
 * signed-in session now exists. For anonymous-allowed routes (`MaybeActor`, e.g. GET /watch):
 * the server reads an expired access token as "signed out" and answers 404, never 401, so
 * `api()`'s own 401 refresh never fires for them.
 */
export function refreshSession(): Promise<boolean> {
  return desktopAuth ? desktopRefreshOnce(desktopAuth) : refreshOnce();
}

/**
 * api<T>(method, path, body?, extraHeaders?) — path is relative to /api/v1 (e.g.
 * "/auth/signin"). Throws ApiFail on any non-2xx response (after the single refresh-and-retry
 * for a 401/"unauthorized"). Returns undefined for 204 No Content responses.
 *
 * `extraHeaders` is how a share-link viewer carries its unlock token (F3/FR-SH5a):
 * `X-Grackle-Unlock`, checked ahead of the `?unlock=` query and the link-scoped cookie by
 * `link_protect::extract_unlock_token` — see `watch/shareGateLogic.ts`.
 */
export async function api<T>(
  method: Method,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  let res = await rawFetch(method, path, body, extraHeaders);

  if (res.status === 401 && path !== "/auth/refresh") {
    const peeked = await toApiFail(res.clone());
    if (peeked.code === "unauthorized") {
      // Desktop: ask the Rust side to redeem the keychain refresh token once; browser: the
      // existing cookie-based `POST /auth/refresh`, coalesced across concurrent callers.
      const refreshed = desktopAuth ? await desktopRefreshOnce(desktopAuth) : await refreshOnce();
      if (refreshed) {
        res = await rawFetch(method, path, body, extraHeaders);
      } else {
        throw peeked;
      }
    }
  }

  if (!res.ok) {
    throw await toApiFail(res);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

/**
 * F13b/W6: a delete blocked by a legal hold comes back as a 409 whose message is the server's
 * own terse `"…: legal_hold"` (see `server/src/grackles.rs::delete_grackle`) — opaque but
 * stable per that module's doc comment. This turns it into the UI copy the design calls for,
 * and otherwise falls back to the server's own message (or a generic one for a non-`ApiFail`).
 */
export function deleteErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiFail) {
    if (err.status === 409 && err.message.includes("legal_hold")) {
      return "This recording is on legal hold and can't be deleted.";
    }
    return err.message;
  }
  return fallback;
}
